import React, { useEffect, useState } from 'react';
import { Dropdown, Form } from 'react-bootstrap';
import DatePicker, { DayValue, Day, Calendar } from 'react-modern-calendar-datepicker';
import 'react-modern-calendar-datepicker/lib/DatePicker.css';
import { default as ReactSelect, components } from 'react-select';
import moment from 'moment';

import { useAuth } from '@/lib/auth';
import { showNotification } from '@/utils/notificationUtils';
import { useAddPet, useUploadPicture } from '../api/addPet';

import { Loader } from '@/components/Loader/Loader';
import { BackNav } from '@/components/BackNavigation/BackNavigation';

import { FileUploader } from './FileUploader';
import { PetType } from './PetType';
import { AppointmentData, Pet, PetWeightConfiguration, Service } from '../types';
import { useGetPetsList } from '../api/getPetsList';
import { PetProfileCard } from './PetProfileCard';
import { useGetPetSettings } from '../api/getPetSettings';

import { ReactComponent as PlusIcon } from '@/assets/img/v2/plus-icon.svg';
import { ReactComponent as BackArrow } from '@/assets/img/back-arrow-red.svg';

import './css/PetProfile.scss';
import './PetProfile.scss';
import Utils from '@/utils/util';
import { DropDown } from '@/components/dropdown/DropDown';
import { useTranslation } from 'react-i18next';
import DatePickerWithPortal from './DatepickerWithPortal';
type Props = {
  fromMenu?: boolean;
  appointmentData?: AppointmentData;
  onSuccess: () => void;
  onNext?: (
    page: 'payment-method' | 'appointment-confirmation',
    appointmentData: AppointmentData
  ) => void;
  onBackClick?: () => void;
  onBack?: (page: 'parent-profile') => void;
};
export const PetProfile = ({ appointmentData, onSuccess, onBack, fromMenu, onNext }: Props) => {
  const petSettingsQuery = useGetPetSettings();
  const useGetPetsMutation = useGetPetsList();
  const addPetMutation = useAddPet();
  const uploadPictureMutation = useUploadPicture();
  const { t } = useTranslation();
  const { user } = useAuth();
  const [breedList, setBreedList] = useState<any[]>();
  const [colorList, setColorList] = useState<any[]>();

  const [addNewPet, setAddNewPet] = useState<boolean>(true);
  const [dataLoaded, setDataLoaded] = useState<boolean>(false);
  const [petSizeList, setPetSizeList] = useState<any[]>();

  const [selectedPet, setSelectedPet] = useState<Pet>({
    name: '',
    breed: Utils.BREED_UNSPECIFIED,
    gender: '',
    weight: '',
    dateOfBirth: '',
    notes: '',
    petsImageUrl: '',
    id: '',
    petType: Utils.getPetTypes(appointmentData?.business?.offerings?.petTypes),
    petColor: '',
    petSize: '',
    petAge:''
  });
  const [selectedPets, setSelectedPets] = useState<Pet[]>([]);
  const [petsList, setPetsList] = useState<Pet[]>();
  const [uploadedImage, setUploadedImage] = useState<string>();
  const [day] = React.useState<Day>({
    day: moment().date(),
    month: moment().month() + 1,
    year: moment().year(),
  } as Day);

  useEffect(() => {
    Utils.scrollToTop();
    fetchPets();
    if (appointmentData && appointmentData.petProfile) {
      setSelectedPet(appointmentData.petProfile);
      setSelectedPets(appointmentData.petProfiles ? [...appointmentData.petProfiles] : []);
    }
  }, []);
  useEffect(() => {
    if (petSettingsQuery.data) {
      setBreedList(petSettingsQuery?.data?.breed_list.map((t: string) => ({ value: t, label: t })));
      setColorList(
        petSettingsQuery?.data?.pet_color_list.map((t: string) => ({ value: t, label: t }))
      );
      formatPetSize(petSettingsQuery?.data.pet_size_defaults);
    }
  }, [petSettingsQuery.data]);

  const formatPetSize = (petWeightConfig: PetWeightConfiguration) => {
    const pwCon: any[] = [];
    for (const [key, value] of Object.entries(petWeightConfig)) {
      pwCon.push({ value: key, label: value.name });
    }
    setPetSizeList(pwCon);
  };

  const fetchPets = async () => {
    const res = await useGetPetsMutation.mutateAsync({
      businessId: user?.businessId,
      page: 1,
      perPage: 100,
      isPast: false,
      petTypes: appointmentData?.business?.offerings?.petTypes,
    });
    setPetsList(res.list);
    setDataLoaded(true);
  };
  useEffect(() => {
    if (petsList && petsList?.length > 0) {
      setAddNewPet(false);
    } else {
      setAddNewPet(true);
    }
  }, [petsList]);

  const onFormValueChange = (formFieldName: string, value: string) => {
    selectedPet[formFieldName as keyof typeof selectedPet] = value;
    setSelectedPet({ ...selectedPet });
  };

  const validate = () => {
    let errorMessage = undefined;
    const { name, dateOfBirth, breed, gender, weight, petColor } = selectedPet;
    if (!name || name.trim().length < 1) {
      errorMessage = t('Please enter pet name');
    } else if (!breed || breed.trim().length < 1) {
      errorMessage = 'Please select the pet breed';
    } else if (!weight || weight.trim().length < 1) {
      errorMessage = 'Please enter pet weight';
    } else if (!dateOfBirth || dateOfBirth.trim().length < 1) {
      errorMessage = t('Please select pet date of birth');
    } else if (!gender || gender.trim().length < 1) {
      errorMessage = t('Please select gender of the pet');
    }
    // else if (!petType || petType.trim().length < 1) {
    //   errorMessage = 'Please select pet type';
    // }
    else if (!petColor || petColor.trim().length < 1) {
      errorMessage = t('Please select color of the pet');
    }

    if (errorMessage) {
      showNotification({
        type: 'info',
        title: errorMessage,
      });
      return false;
    } else {
      return true;
    }
  };

  const onAddPetToServer = async (goBack = false) => {
    if (addNewPet) {
      if (validate() && !addPetMutation.isLoading) {
        const {
          name,
          petType,
          breed,
          dateOfBirth,
          gender,
          weight,
          petColor,
          notes,
          petsImageUrl,
          petSize,
          petAge
        } = selectedPet;

        const data = await addPetMutation.mutateAsync({
          data: {
            name,
            dateOfBirth,
            weight,
            breed,
            gender,
            notes,
            petsImageUrl,
            petType,
            petColor,
            petSize,
            petAge
          },
        });

        if (data && data.petData && appointmentData) {
          const petData = data.petData;
          selectedPet.id = petData.id + '';
          appointmentData.petProfile = selectedPet;
          petsList?.push(selectedPet);
          petsList && setPetsList([...petsList]);
          if (!goBack) {
            if (!appointmentData.petProfiles || appointmentData.petProfiles.length == 0) {
              appointmentData.petProfiles = [selectedPet];
            } else {
              const addedPets = appointmentData.petProfiles;
              addedPets.push(selectedPet);
              setSelectedPets([...addedPets]);
              appointmentData.petProfiles = [...addedPets];
            }
          }
          let servicePrice = 0;
          if (appointmentData.selectedServiceType === Utils.SERVICE_TYPES.GROOMING) {
            servicePrice = Utils.getPriceFromService(
              appointmentData.service,
              appointmentData.petProfile?.petSize
            );
          } else {
            servicePrice = appointmentData.cost ? appointmentData.cost?.fee : 0;
          }
          if (goBack) {
            setAddNewPet(false);
            fetchPets();
          } else {
            onNext &&
              onNext(
                servicePrice > 0 ? 'payment-method' : 'appointment-confirmation',
                appointmentData
              );
          }
        }
      }
    } else {
      // if (selectedPets.length <= 0 || !selectedPet.id) {
      //   showNotification({
      //     type: 'info',
      //     title: 'Please select the pet to continue.',
      //   });
      //   return;
      // }
      if (appointmentData) {
        appointmentData.petProfile = selectedPet;
        appointmentData.petProfiles = selectedPets;
        let servicePrice = 0;
        if (appointmentData?.selectedServiceType === Utils.SERVICE_TYPES.GROOMING) {
          servicePrice = Utils.getPriceFromService(
            appointmentData.service,
            appointmentData.petProfile?.petSize
          );
        } else {
          servicePrice = appointmentData?.cost?.fee ? appointmentData?.cost?.fee : 0;
        }
        onNext &&
          onNext(servicePrice > 0 ? 'payment-method' : 'appointment-confirmation', appointmentData);
      }
    }
  };

  const renderCustomInput = ({ ref }: any) => (
    <input
      readOnly
      ref={ref} // necessary
      placeholder={t('Select date of birth')}
      value={
        selectedPet?.dateOfBirth
          ? moment(selectedPet?.dateOfBirth).format('DD-MMM-YYYY')
          : 'Select date of birth'
      }
      className={`cust-input-mobile ${selectedPet?.dateOfBirth ? '' : 'placeholder-text'}`} // a styling class
    />
  );
  const onProfilePicUpload = async (formData: FormData) => {
    const response = await uploadPictureMutation.mutateAsync({
      file: formData,
    });
    //setImageUrl(response ? response.imageUrl : '');
    onFormValueChange('petsImageUrl', response ? response.imageUrl : '');
  };

  const onPetTypeChange = (petType: string) => {
    onFormValueChange('petType', petType);
  };
  const onPetSelect = (pet: Pet) => {
    const isServiceable = !appointmentData?.service
      ? true
      : appointmentData?.service && isPetSizeServiceable(appointmentData?.service, pet.petSize);
    if (isServiceable) {
      setSelectedPet(pet);
      if (appointmentData?.selectedServiceType !== Utils.SERVICE_TYPES.GROOMING) {
        if (selectedPets) {
          const filteredIndex = selectedPets.findIndex((sp) => sp.id == pet.id);
          if (filteredIndex < 0) {
            const maxPetsAllowed = appointmentData?.noOfPets ? appointmentData?.noOfPets : 1;
            if (selectedPets.length < maxPetsAllowed) {
              selectedPets.push(pet);
            } else {
              showNotification({
                type: 'info',
                title: 'You cannot select more pet than request count for booking.',
              });
            }
          } else {
            selectedPets.splice(filteredIndex, 1);
          }
          setSelectedPets([...selectedPets]);
        }
      } else {
        setSelectedPets([pet]);
      }
    } else {
      showNotification({
        type: 'info',
        title: t('Selected service not available for this pet'),
      });
    }
  };

  const isPetSizeServiceable = (service: Service, petSize?: string) => {
    const petSizeConfiguration = service.petSizePricing;
    if (petSize && petSizeConfiguration) {
      const filteredConfig = petSizeConfiguration[petSize as keyof typeof petSizeConfiguration];
      if (filteredConfig && filteredConfig.price > 0) {
        return true;
      }
      return false;
    } else {
      return true;
    }
  };
  const onBackClick = () => {
    if (addNewPet && petsList && petsList?.length > 0) {
      setAddNewPet(false);
      setSelectedPet({
        name: '',
        breed: Utils.BREED_UNSPECIFIED,
        gender: '',
        weight: '',
        dateOfBirth: '',
        notes: '',
        petsImageUrl: '',
        id: '',
        petType: Utils.getPetTypes(appointmentData?.business?.offerings?.petTypes),
        petColor: '',
        petSize: '',
        fee: '',
        petAge: ''
      });
    } else {
      onBack && onBack('parent-profile');
    }
  };
  const addPet = (e: any) => {
    setSelectedPet({
      name: '',
      breed: Utils.BREED_UNSPECIFIED,
      gender: '',
      weight: '',
      dateOfBirth: '',
      notes: '',
      petsImageUrl: '',
      id: '',
      petType: Utils.getPetTypes(appointmentData?.business?.offerings?.petTypes),
      petColor: '',
      petSize: '',
      petAge: ''
    });
    setAddNewPet(true);
    // if (appointmentData?.selectedServiceType !== Utils.SERVICE_TYPES.GROOMING) {
    // }
  };
  const Option = (props: any) => {
    return (
      <div>
        <components.Option {...props}>
          <div className="option-label">{props.label}</div>
        </components.Option>
      </div>
    );
  };
  const isPetSelected = (pet: Pet) => {
    let filteredIndex = -1;
    if (selectedPets) {
      filteredIndex = selectedPets?.findIndex((sp) => sp.id == pet.id);
    }
    return filteredIndex >= 0 ? true : false;
  };
  const onBackToPets = () => {
    setAddNewPet(false);
  };
  const defaultPetType = Utils.getPetTypes(appointmentData?.business?.offerings?.petTypes);

  return dataLoaded ? (
    <div className="pet-profile-page">
      <BackNav onClick={onBackClick} />
      <div className={`profile-container ${addNewPet ? 'add-pet' : ''}`}>
        <div>
          <div className="header">
            <h5>{addNewPet ? t('Pet Profile') : t('Select Pet')}</h5>
            <span onClick={addPet}>{addNewPet ? '' : t('+ Add new pet')}</span>
          </div>
          {addNewPet ? (
            <>
              <FileUploader
                key={'pet-profile-pic'}
                onUpload={onProfilePicUpload}
                url={selectedPet.petsImageUrl}
                isUploading={uploadPictureMutation.isLoading}
              />
              <div className="petsked-form">
                <div className="form-field personal">
                  <label className="form-label">{t('Select your pet type')}</label>
                  <PetType
                    onChange={onPetTypeChange}
                    value={selectedPet?.petType}
                    defaultPetType={defaultPetType}
                  />
                </div>
                <div className="form-field mb-4">
                  <label className="form-label">{t('Enter name and breed of your pet')}</label>
                  <div className="split full pet spacer">
                    <input
                      type="text"
                      placeholder={t('Pet Name')}
                      className="pet-name"
                      value={selectedPet.name}
                      onChange={(e: any) => onFormValueChange('name', e.target.value)}
                    />
                    <DropDown
                      placeholder={t('Select breed')}
                      selectedValue={selectedPet.breed}
                      data={breedList ? breedList : []}
                      onSelect={(value: any) => {
                        onFormValueChange('breed', value);
                      }}
                      onDismiss={() => {}}
                      enableSearch={true}
                    />
                    
                  </div>
                 
                <div className="form-field">
                  <label className="form-label">{t('Enter some extra details below')}</label>
                  <div className="split full spacer pet">
                  <input
                      type="text"
                      placeholder={t('Pet age')}
                      className="pet-name"
                      value={selectedPet.petAge}
                      onChange={(e: any) => onFormValueChange('petAge', e.target.value)}
                    />
                    <DropDown
                      placeholder={t('Select pet size')}
                      selectedValue={selectedPet?.petSize}
                      data={petSizeList ? petSizeList : []}
                      onSelect={(value: any) => {
                        onFormValueChange('petSize', value);
                      }}
                      onDismiss={() => {}}
                      enableSearch={false}
                    />
                   
                  </div>
                </div>
                  <div className="split full spacer pet">
                    <DatePickerWithPortal
                      value={
                        selectedPet?.dateOfBirth ? selectedPet?.dateOfBirth : undefined
                      }
                      onChange={(val: DayValue) => {
                        console.log('val', val);
                        onFormValueChange('dateOfBirth', `${val?.month}/${val?.day}/${val?.year}`);
                      }}
                      placeholder={t('Select date of birth')}
                      maximumDate={day}

                    />
                    <DropDown
                      placeholder="Select pet color"
                      selectedValue={selectedPet?.petColor}
                      data={colorList ? colorList : []}
                      onSelect={(value: any) => {
                        onFormValueChange('petColor', value);
                      }}
                      onDismiss={() => {}}
                      enableSearch={true}
                    />
                    {/* <ReactSelect
                      classNamePrefix={'petsked-multiselect'}
                      className="multiselect-react"
                      isMulti={false}
                      options={colorList ? colorList : []}
                      placeholder={'Select Color'}
                      components={{
                        Option,
                      }}
                      onChange={(selected: any) => {
                        onFormValueChange('petColor', selected.value);
                      }}
                      value={colorList?.find((cl) => cl.value == selectedPet.petColor)}
                      menuPlacement="auto"
                    /> */}
                  </div>
                  <div className="split full">
                    <span className="pet-weight-container">
                      <input
                        type="text"
                        placeholder={t('Enter Weight')}
                        onChange={(e: any) => onFormValueChange('weight', e.target.value)}
                      />
                      <span>{t('lbs')}</span>
                    </span>
                    <div className="sex">
                      <Form.Check
                        inline
                        value={'male'}
                        checked={selectedPet.gender?.toLowerCase() === 'male' ? true : false}
                        label={t('Male')}
                        name="group1"
                        type="radio"
                        onChange={(e: any) => onFormValueChange('gender', e.target.value)}
                      />
                      <span className="seperator"></span>
                      <Form.Check
                        inline
                        value={'female'}
                        checked={selectedPet.gender?.toLowerCase() === 'female' ? true : false}
                        label={t('Female')}
                        name="group1"
                        type="radio"
                        onChange={(e: any) => onFormValueChange('gender', e.target.value)}
                      />
                    </div>
                  </div>
                  <textarea
                    rows={4}
                    value={selectedPet.notes}
                    placeholder={t('Add note')}
                    onChange={(e: any) => onFormValueChange('notes', e.target.value)}
                  />
                </div>
                <hr />
              </div>
            </>
          ) : (
            <>
              <p className="petlist-desc">
                Choose the pet you want{' '}
                {appointmentData?.selectedServiceType === Utils.SERVICE_TYPES.GROOMING
                  ? 'serviced'
                  : 'to book'}
              </p>
              <div className="petList">
                {petsList?.map((pl, index) => (
                  <PetProfileCard
                    key={`pet-profile-card${index}`}
                    data={pl}
                    onSelect={onPetSelect}
                    isSelected={isPetSelected(pl)}
                  />
                ))}
              </div>
            </>
          )}
        </div>

        <div className="action">
          {addNewPet && (
            <>
              <button className="petsked-button button-no-bg icon" onClick={onBackToPets}>
                <BackArrow />
                Back to pets
              </button>
              <button
                className="petsked-button button-light icon"
                onClick={() => onAddPetToServer(true)}
                disabled={addPetMutation.isLoading}
              >
                {addPetMutation.isLoading ? (
                  <Loader />
                ) : (
                  <>
                    <PlusIcon />
                    <span>Add pet</span>
                  </>
                )}
              </button>
            </>
          )}
          {!addNewPet && (
            <button
              className="petsked-button button-dark"
              onClick={() => onAddPetToServer()}
              disabled={addPetMutation.isLoading}
            >
              {addPetMutation.isLoading ? <Loader /> : <span>Continue</span>}
            </button>
          )}
        </div>
      </div>
    </div>
  ) : (
    <></>
  );
};
